/**
 * Navigation Continuity Telemetry
 *
 * Lightweight instrumentation for tracking overlay lifecycle, restoration
 * success/failure, and fallback usage. Designed to surface silent
 * continuity failures during rollout.
 *
 * In development: logs to console with structured data.
 * In production: forwards to the registered telemetry sink (if any).
 *
 * @module lib/navigation/telemetry
 */

// ── Types ─────────────────────────────────────────────────────────────────

export type TelemetryEvent =
    | 'overlay-opened'
    | 'overlay-closed'
    | 'overlay-close-sequenced'
    | 'return-policy-resolved'
    | 'fallback-canonical-used'
    | 'highlight-restore-success'
    | 'highlight-restore-failed'
    | 'focus-restore-success'
    | 'focus-restore-failed'
    | 'source-highlight-activated'
    | 'source-highlight-cleared'
    | 'scroll-restored'
    | 'scroll-restore-failed'
    | 'deep-link-consumed'
    | 'idempotent-open-blocked'
    | 'nested-close-cascade'
    | 'orphaned-overlay-detected';

export interface TelemetryPayload {
    event: TelemetryEvent;
    data: Record<string, unknown>;
    timestamp: number;
}

export type TelemetrySink = (payload: TelemetryPayload) => void;

// ── State ─────────────────────────────────────────────────────────────────

const sinks: TelemetrySink[] = [];
let enabled = true;
let devMode = false;

try {
    // Auto-detect dev mode from Vite env
    devMode = import.meta.env?.DEV === true;
} catch {
    // Not in Vite context — default disabled
}

// ── Counters for diagnostics ──────────────────────────────────────────────

const counters = new Map<TelemetryEvent, number>();

/**
 * Per-surface counters. Tracks { surfaceName → { event → count } }.
 * Answers "which surface fails focus restore most?" without log parsing.
 */
const surfaceCounters = new Map<string, Map<TelemetryEvent, number>>();

/** Recent payloads ring buffer for debugging (dev only). */
const RECENT_BUFFER_SIZE = 50;
const recentPayloads: TelemetryPayload[] = [];

// ── Public API ────────────────────────────────────────────────────────────

/**
 * Emit a telemetry event. Called by navigation modules internally.
 *
 * In dev mode: logs to console group.
 * If sinks are registered: forwards to all sinks.
 * If disabled or no sinks: no-op (zero cost).
 */
export function emitTelemetry(
    event: TelemetryEvent,
    data: Record<string, unknown> = {},
): void {
    if (!enabled) return;

    // Increment global counter
    counters.set(event, (counters.get(event) ?? 0) + 1);

    // Increment per-surface counter (uses 'surface' field from data)
    const surface = typeof data.surface === 'string' ? data.surface : undefined;
    if (surface) {
        let surfMap = surfaceCounters.get(surface);
        if (!surfMap) {
            surfMap = new Map();
            surfaceCounters.set(surface, surfMap);
        }
        surfMap.set(event, (surfMap.get(event) ?? 0) + 1);
    }

    const payload: TelemetryPayload = {
        event,
        data,
        timestamp: Date.now(),
    };

    // Ring buffer (dev diagnostics)
    if (devMode) {
        recentPayloads.push(payload);
        if (recentPayloads.length > RECENT_BUFFER_SIZE) {
            recentPayloads.shift();
        }
    }

    // Dev console logging
    if (devMode) {
        const isError = event.includes('failed') || event.includes('orphaned');
        const method = isError ? console.warn : console.debug;
        method(`[nav:${event}]`, data);
    }

    // Forward to registered sinks
    for (const sink of sinks) {
        try {
            sink(payload);
        } catch {
            // Never let a sink error break the navigation system
        }
    }
}

/**
 * Register a telemetry sink. Returns unsubscribe function.
 *
 * @example
 * // Forward to analytics
 * registerTelemetrySink((payload) => {
 *   analytics.track(`nav_${payload.event}`, payload.data);
 * });
 */
export function registerTelemetrySink(sink: TelemetrySink): () => void {
    sinks.push(sink);
    return () => {
        const idx = sinks.indexOf(sink);
        if (idx >= 0) sinks.splice(idx, 1);
    };
}

/** Enable or disable telemetry globally. */
export function setTelemetryEnabled(value: boolean): void {
    enabled = value;
}

/** Enable or disable dev console logging. */
export function setTelemetryDevMode(value: boolean): void {
    devMode = value;
}

/**
 * Get all telemetry counters. Useful for diagnostics dashboard.
 *
 * @example
 * getTelemetryCounters()
 * // => { 'overlay-opened': 12, 'overlay-closed': 11, 'highlight-restore-failed': 1 }
 */
export function getTelemetryCounters(): Record<string, number> {
    const result: Record<string, number> = {};
    counters.forEach((count, event) => {
        result[event] = count;
    });
    return result;
}

/** Reset all counters (for testing). */
export function resetTelemetryCounters(): void {
    counters.clear();
    surfaceCounters.clear();
    recentPayloads.length = 0;
}

/**
 * Get per-surface breakdown of telemetry counters.
 * Answers: "which surface fails most?" / "which surface still acts legacy?"
 */
export function getSurfaceCounters(): Record<string, Record<string, number>> {
    const result: Record<string, Record<string, number>> = {};
    surfaceCounters.forEach((events, surface) => {
        const entry: Record<string, number> = {};
        events.forEach((count, event) => { entry[event] = count; });
        result[surface] = entry;
    });
    return result;
}

/**
 * Get recent telemetry payloads (ring buffer, dev only).
 * Useful for inspecting the exact sequence of events that led to a bug.
 */
export function getRecentPayloads(): readonly TelemetryPayload[] {
    return recentPayloads;
}

/**
 * Get a diagnostic snapshot of the navigation system health.
 * Call from browser devtools: `window.__NAV_DIAGNOSTICS__()`
 *
 * Answers the 4 key rollout questions:
 *  1. Which return policies are actually being used?
 *  2. How often is canonical fallback triggered?
 *  3. Which focus restores fail most?
 *  4. Which surfaces still behave like legacy?
 */
export function getDiagnosticSnapshot(): {
    counters: Record<string, number>;
    failureRate: number;
    returnPolicyUsage: Record<string, number>;
    canonicalFallbackRate: number;
    failuresBySurface: Record<string, number>;
    enabled: boolean;
    sinksRegistered: number;
} {
    const c = getTelemetryCounters();
    const totalRestores = (c['highlight-restore-success'] ?? 0) +
        (c['highlight-restore-failed'] ?? 0);
    const failureRate = totalRestores > 0
        ? (c['highlight-restore-failed'] ?? 0) / totalRestores
        : 0;

    // Return policy usage breakdown from per-surface counters
    const returnPolicyUsage: Record<string, number> = {};
    recentPayloads
        .filter((p) => p.event === 'return-policy-resolved')
        .forEach((p) => {
            const policy = String(p.data.returnPolicy ?? 'unknown');
            returnPolicyUsage[policy] = (returnPolicyUsage[policy] ?? 0) + 1;
        });

    // Canonical fallback rate
    const totalCloses = c['overlay-closed'] ?? 0;
    const fallbackCount = c['fallback-canonical-used'] ?? 0;
    const canonicalFallbackRate = totalCloses > 0
        ? fallbackCount / totalCloses
        : 0;

    // Focus restore failures grouped by surface
    const failuresBySurface: Record<string, number> = {};
    surfaceCounters.forEach((events, surface) => {
        const failures = events.get('highlight-restore-failed') ?? 0;
        if (failures > 0) failuresBySurface[surface] = failures;
    });

    return {
        counters: c,
        failureRate,
        returnPolicyUsage,
        canonicalFallbackRate,
        failuresBySurface,
        enabled,
        sinksRegistered: sinks.length,
    };
}

// ── DevTools integration ──────────────────────────────────────────────────
// Expose diagnostic function on window in dev mode (safe no-op in SSR/test)
if (devMode && typeof window !== 'undefined') {
    (window as unknown as Record<string, unknown>).__NAV_DIAGNOSTICS__ = getDiagnosticSnapshot;
    (window as unknown as Record<string, unknown>).__NAV_COUNTERS__ = getTelemetryCounters;
    (window as unknown as Record<string, unknown>).__NAV_SURFACE_COUNTERS__ = getSurfaceCounters;
    (window as unknown as Record<string, unknown>).__NAV_RECENT__ = getRecentPayloads;
}
